import { useState, useCallback } from "react";
import { ApiResponse } from "../../services/Dtos";
import { ApiService } from "../../services/ApiService";
import useAxios from "../useAxios";
import { useSchoolsContext } from "../../Contexts/schoolContext";
import { useApiResponseHandler } from "../useApiResponseHandler";



export const useSchoolService = () => {
    const { setSchool } = useSchoolsContext();
  const axiosClient = useAxios();
  const [isLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentTermLoading, setCurrentTermLoading] = useState(false);
  const [currentTermDaysOpenLoading, setCurrentTermDaysOpenLoading] =
    useState(false);
  const [nextTermLoading, setNextTermLoading] = useState(false);
  const [nextTermStartDateLoading, setNextTermStartDateLoading] =
    useState(false);
  const [currentTermStartDateLoading, setCurrentTermStartDateLoading] =
    useState(false);
  const [currentTermEndDateLoading, setCurrentTermEndDateLoading] =
    useState(false);
    const [leapTermLoading, setLeapTermLoading] = useState(false);
    const [currentSessionLoading, setCurrentSessionLoading] = useState(false);
  const { handleApiError, handleApiSuccess, clearErrors } =
    useApiResponseHandler({
      setErrors,
    });

  const updateCurrentTerm = useCallback(
    async (
      termId: string,
      onSuccess?: (termId: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setCurrentTermLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<{ currentTermId: string }>(
          "/schools",
          axiosClient
        ).update("", { currentTermId: termId });
        if (result.succeeded) {
          setSchool((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              currentTermId: termId,
            };
          });
          handleApiSuccess("Current term updated successfully");
          onSuccess?.(termId);
          return { result };
        } else {
          handleApiError(result, "Failed to update current term");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update current term");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update current term",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setCurrentTermLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess]
  );

  const updateCurrentTermDaysOpen = useCallback(
    async (
      daysOpen: number,
      onSuccess?: (daysOpen: number) => void
    ): Promise<{ result: ApiResponse }> => {
      setCurrentTermDaysOpenLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<{ currentTermDaysOpen: number }>(
          "/schools",
          axiosClient
        ).update("", { currentTermDaysOpen: daysOpen });
        if (result.succeeded) {
          setSchool((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              currentTermDaysOpen: daysOpen,
            };
          });
          handleApiSuccess("Current term days open updated successfully");
          onSuccess?.(daysOpen);
          return { result };
        } else {
          handleApiError(result, "Failed to update current term days open");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update current term days open");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update current term days open",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setCurrentTermDaysOpenLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess]
  );

  const updateNextTerm = useCallback(
    async (
      termId: string,
      onSuccess?: (termId: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setNextTermLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<{ nextTermId: string }>(
          "/schools",
          axiosClient
        ).update("", { nextTermId: termId });
        if (result.succeeded) {
          setSchool((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              nextTermId: termId,
            };
          });
          handleApiSuccess("Next term updated successfully");
          onSuccess?.(termId);
          return { result };
        } else {
          handleApiError(result, "Failed to update next term");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update next term");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update next term",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setNextTermLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess]
  );

  const updateNextTermStartDate = useCallback(
    async (
      startDate: string,
      onSuccess?: (startDate: string) => void,
    ): Promise<{ result: ApiResponse }> => {
      setNextTermStartDateLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<{ nextTermStartDate: string }>(
          "/schools",
          axiosClient
        ).update("", { nextTermStartDate: startDate });
        if (result.succeeded) {
          setSchool((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              nextTermStartDate: startDate,
            };
          });
          handleApiSuccess("Next term start date updated successfully");
          onSuccess?.(startDate);
          return { result };
        } else {
          handleApiError(result, "Failed to update next term start date");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update next term start date");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update next term start date",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setNextTermStartDateLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess]
  );

  const updateCurrentTermStartDate = useCallback(
    async (
      startDate: string,
      onSuccess?: (startDate: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setCurrentTermStartDateLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<{ currentTermStartDate: string }>(
          "/schools",
          axiosClient
        ).update("", { currentTermStartDate: startDate });
      if (result.succeeded) {
        setSchool((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            currentTermStartDate: startDate,
          };
        });
        handleApiSuccess("Current term start date updated successfully");
        onSuccess?.(startDate);
        return { result };
      } else {
        handleApiError(result, "Failed to update current term start date");
        return { result };
      }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update current term start date");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update current term start date",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setCurrentTermStartDateLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess]
  );

  const updateCurrentTermEndDate = useCallback(

    async (
      endDate: string,
      onSuccess?: (endDate: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setCurrentTermEndDateLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<{ currentTermEndDate: string }>(
          "/schools",
          axiosClient
        ).update("", { currentTermEndDate: endDate });
        if (result.succeeded) {
          setSchool((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              currentTermEndDate: endDate,
            };
          });
          handleApiSuccess("Current term end date updated successfully");
          onSuccess?.(endDate);
          return { result };
        } else {
          handleApiError(result, "Failed to update current term end date");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update current term end date");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update current term end date",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setCurrentTermEndDateLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess]
  );

  const updateLeapTerm = useCallback(

    async (
      leapTermId: string,
      onSuccess?: (leapTermId: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setLeapTermLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<{ leapTermId: string }>(
          "/schools",
          axiosClient
        ).update("", { leapTermId: leapTermId });
        if (result.succeeded) {
          setSchool((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              leapTermId: leapTermId,
            };
          });
          handleApiSuccess("Leap term updated successfully");
          onSuccess?.(leapTermId);
          return { result };
        } else {
          handleApiError(result, "Failed to update leap term");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update leap term");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update leap term",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setLeapTermLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess]
  );

  const updateCurrentSession = useCallback(

    async (
      sessionId: string,
      onSuccess?: (sessionId: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setCurrentSessionLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<{ currentSessionId: string }>(
          "/schools",
          axiosClient
        ).update("", { currentSessionId: sessionId });
        if (result.succeeded) {
          setSchool((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              currentSessionId: sessionId,
            };
          });
          handleApiSuccess("Current session updated successfully");
          onSuccess?.(sessionId);
          return { result };
        } else {
          handleApiError(result, "Failed to update current session");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update current session");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update current session",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setCurrentSessionLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess]
  );

  return {
    updateCurrentTerm,
    updateCurrentTermDaysOpen,
    updateNextTerm,
    updateNextTermStartDate,
    updateCurrentTermStartDate,
    updateCurrentTermEndDate,
    updateLeapTerm,
    updateCurrentSession,
    currentTermLoading,
    currentTermDaysOpenLoading,
    nextTermLoading,
    nextTermStartDateLoading,
    currentTermStartDateLoading,
    currentTermEndDateLoading,
    leapTermLoading,
    currentSessionLoading,
    isLoading,
    errors,
    setErrors,
    clearErrors,
    openDeleteDialog,
    setOpenDeleteDialog,
  };
};
